import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Zap, CircleDot, Target, Waves, Route, Mountain,
  HeartPulse, Wind, Focus, Shield, Atom, Apple, Hand, Clock,
  Sparkles, Lock, Check, Award, Crown
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";

const iconMap = {
  'zap': Zap,
  'circle-dot': CircleDot,
  'target': Target,
  'waves': Waves,
  'route': Route,
  'mountain': Mountain,
  'heart-pulse': HeartPulse,
  'wind': Wind,
  'focus': Focus,
  'shield': Shield,
  'atom': Atom,
  'apple': Apple,
  'hand': Hand,
  'clock': Clock,
  'sparkles': Sparkles
};

const EnergyMechanicsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState({ completed: 0, total: 15, percentage: 0 });
  const [isPremium, setIsPremium] = useState(false);
  const [hasBadge, setHasBadge] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchModules(); }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/energy-mechanics/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModules(response.data.modules);
      setProgress(response.data.progress);
      setIsPremium(response.data.is_premium);
      setHasBadge(response.data.has_completion_badge);
    } catch (error) {
      console.error("Error fetching modules:", error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || Zap;
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-rose-500/20 flex items-center justify-center">
            <Zap className="w-8 h-8 text-rose-600" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="energy-mechanics-page">
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Energy Mechanics</span>
          </div>
          <div className="w-24" />
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-6">
            <Zap className="w-10 h-10 text-rose-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Energy Mechanics
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-2">
            Understanding the Human Energy System
          </p>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            15 lessons exploring the science and wisdom of how energy relates to us as human beings.
          </p>
        </div>

        {/* Progress */}
        <Card className="glass-card rounded-2xl border-0 mb-10 bg-gradient-to-r from-rose-50/50 to-orange-50/50 dark:from-rose-950/20 dark:to-orange-950/20">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-rose-500/20 flex items-center justify-center">
                  {hasBadge ? (
                    <Award className="w-7 h-7 text-rose-600" />
                  ) : (
                    <Zap className="w-7 h-7 text-rose-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-foreground">
                    {hasBadge ? "Energy Master!" : "Your Progress"}
                  </h3>
                  <p className="text-muted-foreground">
                    {progress.completed} of {progress.total} lessons completed
                  </p>
                </div>
              </div>
              <div className="w-full md:w-64">
                <Progress value={progress.percentage} className="h-3 bg-rose-100 dark:bg-rose-900/30" />
                <p className="text-sm text-muted-foreground text-center mt-2">{progress.percentage}% complete</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Premium CTA */}
        {!isPremium && (
          <Card className="glass-card rounded-2xl border-0 mb-10 bg-gradient-to-r from-primary/10 to-accent/10">
            <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Crown className="w-10 h-10 text-primary" />
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    Unlock All 15 Lessons
                  </h3>
                  <p className="text-muted-foreground">
                    7 lessons are free. Go premium to access all 15 and complete your energy mastery journey!
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate("/subscription")} className="btn-primary">
                Go Premium
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Intro Quote */}
        <Card className="glass-card rounded-2xl border-0 mb-10 bg-rose-50/30 dark:bg-rose-950/10">
          <CardContent className="p-6 text-center">
            <p className="text-lg italic text-foreground mb-2">
              "If you want to find the secrets of the universe, think in terms of energy, frequency and vibration."
            </p>
            <span className="text-sm text-rose-600">— Nikola Tesla</span>
          </CardContent>
        </Card>

        {/* Free Lessons */}
        <div className="mb-10">
          <h2 className="font-heading text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <Zap className="w-6 h-6 text-rose-600" /> Foundation Lessons (Free)
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {modules.filter(m => !m.is_premium).map((module) => {
              const Icon = getIcon(module.icon);
              return (
                <Card
                  key={module.id}
                  className={`glass-card rounded-2xl border-0 cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
                    module.is_completed ? 'ring-2 ring-rose-500/50 bg-rose-50/30 dark:bg-rose-950/20' : ''
                  }`}
                  onClick={() => navigate(`/energy-mechanics/${module.id}`)}
                  data-testid={`energy-module-${module.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        module.is_completed ? 'bg-rose-500/20' : 'bg-rose-100 dark:bg-rose-900/30'
                      }`}>
                        {module.is_completed ? (
                          <Check className="w-6 h-6 text-rose-600" />
                        ) : (
                          <Icon className="w-6 h-6 text-rose-600" strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-muted-foreground">Lesson {module.id}</span>
                          <span className="text-xs text-muted-foreground">• {module.estimated_time}</span>
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-foreground mb-1">
                          {module.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {module.subtitle}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Premium Lessons */}
        <div>
          <h2 className="font-heading text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <Crown className="w-6 h-6 text-primary" /> Advanced Lessons (Premium)
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {modules.filter(m => m.is_premium).map((module) => {
              const Icon = getIcon(module.icon);
              return (
                <Card
                  key={module.id}
                  className={`glass-card rounded-2xl border-0 transition-all duration-300 ${
                    module.is_locked
                      ? 'opacity-70 cursor-not-allowed'
                      : 'cursor-pointer hover:scale-[1.02]'
                  } ${module.is_completed ? 'ring-2 ring-rose-500/50 bg-rose-50/30 dark:bg-rose-950/20' : ''}`}
                  onClick={() => !module.is_locked && navigate(`/energy-mechanics/${module.id}`)}
                  data-testid={`energy-module-${module.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        module.is_completed
                          ? 'bg-rose-500/20'
                          : module.is_locked
                            ? 'bg-muted'
                            : 'bg-rose-100 dark:bg-rose-900/30'
                      }`}>
                        {module.is_completed ? (
                          <Check className="w-6 h-6 text-rose-600" />
                        ) : module.is_locked ? (
                          <Lock className="w-6 h-6 text-muted-foreground" />
                        ) : (
                          <Icon className="w-6 h-6 text-rose-600" strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-muted-foreground">Lesson {module.id}</span>
                          <span className="text-xs text-muted-foreground">• {module.estimated_time}</span>
                          {module.is_locked && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                              Premium
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-foreground mb-1">
                          {module.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {module.subtitle}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Completion */}
        {hasBadge && (
          <Card className="glass-card rounded-2xl border-0 mt-10 bg-gradient-to-r from-rose-500/10 to-orange-500/10 text-center">
            <CardContent className="p-8">
              <div className="w-20 h-20 rounded-full bg-rose-500/20 flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-rose-600" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">
                Congratulations, Energy Master!
              </h3>
              <p className="text-muted-foreground">
                You've completed all 15 Energy Mechanics lessons and earned your Energy Mastery Badge!
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default EnergyMechanicsPage;
